import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { 
  Users, 
  ArrowRight, 
  Loader2, 
  FileText, 
  MessageSquare, 
  Mail, 
  Presentation,
  Copy,
  Download,
  RefreshCw,
  Sparkles,
  Edit3,
  Settings,
  CheckCircle,
  Home
} from 'lucide-react'
import { useJourneyStore } from '@/stores/journeyStore'
import { generateId } from '@/lib/utils'

interface ContentType {
  id: string
  name: string
  icon: React.ComponentType<any>
  description: string
  template: string
  examples: string[]
}

interface CollaborationMessage {
  id: string
  type: 'user' | 'ai' | 'system'
  content: string
  timestamp: Date
}

const contentTypes: ContentType[] = [
  {
    id: 'blog',
    name: 'Blog Post',
    icon: FileText,
    description: 'Create engaging blog content with AI assistance',
    template: 'Write a blog post about [TOPIC] that [GOAL].',
    examples: [
      'Write a blog post about AI in healthcare that educates patients about benefits',
      'Create a blog post about remote work productivity that provides actionable tips',
      'Write a blog post about sustainable business practices that inspires action'
    ]
  },
  {
    id: 'social',
    name: 'Social Media',
    icon: MessageSquare,
    description: 'Craft compelling social media content',
    template: 'Create a [PLATFORM] post about [TOPIC] that [TONE].',
    examples: [
      'Create a LinkedIn post about leadership lessons that inspires professionals',
      'Write a Twitter thread about productivity hacks that provides value',
      'Create an Instagram caption about team success that celebrates achievements'
    ]
  },
  {
    id: 'email',
    name: 'Email Campaign',
    icon: Mail,
    description: 'Design effective email marketing content',
    template: 'Write an email to [AUDIENCE] about [TOPIC] that [ACTION].',
    examples: [
      'Write an email to customers about a product launch that drives sales',
      'Create an email to team members about policy changes that ensures clarity',
      'Write a newsletter to subscribers about industry trends that provides insights'
    ]
  },
  {
    id: 'presentation',
    name: 'Presentation',
    icon: Presentation,
    description: 'Build presentation outlines and key talking points',
    template: 'Create a presentation outline about [TOPIC] for [AUDIENCE].',
    examples: [
      'Create a presentation outline about digital transformation for executives',
      'Build a presentation about AI adoption for technical teams',
      'Design a presentation about market strategy for investors'
    ]
  }
]

export function Stage2Collaboration() {
  const navigate = useNavigate()
  const { completeStage, updateStageProgress } = useJourneyStore()
  const [selectedContentType, setSelectedContentType] = useState<ContentType | null>(null)
  const [currentContent, setCurrentContent] = useState('')
  const [collaborationHistory, setCollaborationHistory] = useState<CollaborationMessage[]>([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [contentSettings, setContentSettings] = useState({
    tone: 'professional',
    length: 'medium',
    style: 'informative'
  })
  const [isCompleting, setIsCompleting] = useState(false)
  const [completionMessage, setCompletionMessage] = useState('')
  const [collaborationCount, setCollaborationCount] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [collaborationHistory])

  const generateAIResponse = async (prompt: string, context: string = '') => {
    // Simulate AI response generation
    const responses = [
      `I'll help you create that content! Here's my suggestion:\n\n${context}\n\nThis approach focuses on engaging your audience while maintaining ${contentSettings.tone} tone. What would you like me to adjust or expand on?`,
      `Great idea! Let me build on that:\n\n${context}\n\nI've structured this to be ${contentSettings.length} length with a ${contentSettings.style} style. Should we refine any particular section?`,
      `Here's a ${contentSettings.style} take on your request:\n\n${context}\n\nThis version emphasizes clarity and engagement. Would you like me to adjust the tone or add more specific details?`
    ]
    
    return responses[Math.floor(Math.random() * responses.length)]
  }

  const handleGenerateContent = async () => {
    if (!selectedContentType || !userInput.trim()) return
    
    setIsGenerating(true)
    
    // Add user message
    const userMessage: CollaborationMessage = {
      id: generateId(),
      type: 'user',
      content: userInput,
      timestamp: new Date()
    }
    
    setCollaborationHistory(prev => [...prev, userMessage])
    
    // Generate content based on type and input
    let generatedContent = ''
    switch (selectedContentType.id) {
      case 'blog':
        generatedContent = `# ${userInput.includes('about') ? userInput.split('about')[1]?.trim() : 'Your Topic'}\n\n## Introduction\n\nIn today's rapidly evolving landscape, understanding this topic is crucial for success. This comprehensive guide will walk you through the key concepts and practical applications.\n\n## Key Points\n\n- **First Point**: Essential foundation knowledge\n- **Second Point**: Practical implementation strategies  \n- **Third Point**: Advanced techniques and best practices\n\n## Actionable Steps\n\n1. Start by assessing your current situation\n2. Implement the core strategies outlined above\n3. Measure results and iterate for improvement\n\n## Conclusion\n\nBy following these guidelines, you'll be well-equipped to tackle this challenge successfully. Remember, consistency and continuous learning are key to long-term success.`
        break
      case 'social':
        generatedContent = `🚀 ${userInput}\n\nHere's what you need to know:\n\n✅ Key insight #1 that drives results\n✅ Actionable tip that you can implement today\n✅ Pro strategy that makes the difference\n\n💡 The secret? Consistency beats perfection every time.\n\nWhat's your experience with this? Share in the comments! 👇\n\n#Leadership #Productivity #Success`
        break
      case 'email':
        generatedContent = `Subject: ${userInput.includes('about') ? userInput.split('about')[1]?.trim() : 'Important Update'}\n\nHi [Name],\n\nI hope this email finds you well. I wanted to reach out personally about something that could significantly impact your [relevant area].\n\n**Here's what's happening:**\n\n${userInput}\n\n**Why this matters to you:**\n- Immediate benefit #1\n- Long-term advantage #2  \n- Competitive edge #3\n\n**Next steps:**\n\nI'd love to discuss how this applies to your specific situation. Would you be available for a brief 15-minute call this week?\n\n[Call to action button]\n\nBest regards,\n[Your name]`
        break
      case 'presentation':
        generatedContent = `# ${userInput.includes('about') ? userInput.split('about')[1]?.trim() : 'Presentation Title'}\n\n## Slide 1: Title Slide\n- Main title\n- Subtitle/tagline\n- Your name and date\n\n## Slide 2: Agenda\n- What we'll cover today\n- Key takeaways\n- Q&A session\n\n## Slide 3: Problem Statement\n- Current challenges\n- Market context\n- Why this matters now\n\n## Slide 4: Solution Overview\n- Our approach\n- Key benefits\n- Unique advantages\n\n## Slide 5: Implementation\n- Step-by-step process\n- Timeline\n- Resources needed\n\n## Slide 6: Results & Impact\n- Expected outcomes\n- Success metrics\n- ROI projections\n\n## Slide 7: Next Steps\n- Action items\n- Timeline\n- Contact information`
        break
    }
    
    setTimeout(async () => {
      const aiResponse = await generateAIResponse(userInput, generatedContent)
      
      const aiMessage: CollaborationMessage = {
        id: generateId(),
        type: 'ai',
        content: aiResponse,
        timestamp: new Date()
      }
      
      setCollaborationHistory(prev => [...prev, aiMessage])
      setCurrentContent(generatedContent)
      setCollaborationCount(prev => prev + 1)
      setUserInput('')
      setIsGenerating(false)
      
      // Update progress
      updateStageProgress(2, {
        interactionData: {
          contentType: selectedContentType.id,
          collaborationCount: collaborationCount + 1,
          contentGenerated: true
        }
      })
    }, 2000)
  }

  const handleRefineContent = async () => {
    if (!currentContent.trim() || !userInput.trim()) return
    
    setIsGenerating(true)
    
    const userMessage: CollaborationMessage = {
      id: generateId(),
      type: 'user',
      content: `Please refine this: ${userInput}`,
      timestamp: new Date()
    }
    
    setCollaborationHistory(prev => [...prev, userMessage])
    
    setTimeout(async () => {
      // Simulate content refinement
      const refinedContent = currentContent + `\n\n## Updated Section\n\nBased on your feedback: "${userInput}", I've enhanced the content to better address your specific needs. This refined version incorporates your suggestions while maintaining the overall structure and flow.`
      
      const aiResponse = await generateAIResponse(userInput, 'I\'ve updated the content based on your feedback!')
      
      const aiMessage: CollaborationMessage = {
        id: generateId(),
        type: 'ai',
        content: aiResponse,
        timestamp: new Date()
      }
      
      setCollaborationHistory(prev => [...prev, aiMessage])
      setCurrentContent(refinedContent)
      setCollaborationCount(prev => prev + 1)
      setUserInput('')
      setIsGenerating(false)
    }, 1500)
  }

  const handleCopyContent = async () => {
    try {
      await navigator.clipboard.writeText(currentContent)
      const systemMessage: CollaborationMessage = {
        id: generateId(),
        type: 'system',
        content: '✅ Content copied to clipboard!',
        timestamp: new Date()
      }
      setCollaborationHistory(prev => [...prev, systemMessage])
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleComplete = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Completing stage...')
    
    try {
      completeStage(2, { 
        contentType: selectedContentType?.id,
        collaborationCount,
        contentGenerated: currentContent.length > 0,
        settingsUsed: contentSettings
      })
      setCompletionMessage('Stage completed! Moving to next stage...')
      
      // Let the store's auto-advancement handle navigation
      // No immediate navigation to dashboard
    } catch (error) {
      console.error('Error completing stage:', error)
      setCompletionMessage('Error completing stage. Please try again.')
      setIsCompleting(false)
    }
  }

  const handleSkip = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Skipping to next stage...')
    
    try {
      completeStage(2, { 
        skipped: true,
        collaborationCount,
        contentType: selectedContentType?.id 
      })
      
      // Let the store's auto-advancement handle navigation
      // No immediate navigation to dashboard
    } catch (error) {
      console.error('Error skipping stage:', error)
      setCompletionMessage('Error skipping stage. Please try again.')
      setIsCompleting(false)
    }
  }

  const canComplete = collaborationCount >= 1 && currentContent.length > 0

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Stage Header */}
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <Users className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Stage 2: AI Creative Partnership</h1>
          <p className="text-lg text-muted-foreground">Collaborate with AI to create professional content</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Content Type Selection */}
        {!selectedContentType && (
          <>
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Choose Your Content Type
                  </CardTitle>
                  <CardDescription>
                    Select the type of content you'd like to create with AI assistance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {contentTypes.map((type) => (
                      <div
                        key={type.id}
                        onClick={() => setSelectedContentType(type)}
                        className="p-4 border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <type.icon className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium mb-1">{type.name}</h3>
                            <p className="text-sm text-muted-foreground mb-3">{type.description}</p>
                            <div className="text-xs text-muted-foreground">
                              <strong>Example:</strong> {type.examples[0]}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Stage Actions - Always Available */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Your Progress</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground">Content type selected:</div>
                      <div className="text-sm font-medium">None</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground">Collaborations:</div>
                      <div className="text-sm font-medium">{collaborationCount}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground">Content generated:</div>
                      <div className="text-sm font-medium">{currentContent ? 'Yes' : 'No'}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Collaboration Interface */}
        {selectedContentType && (
          <>
            {/* Left Panel - Settings & Chat */}
            <div className="space-y-6">
              {/* Content Type Info */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <selectedContentType.icon className="w-5 h-5 text-primary" />
                      <CardTitle className="text-lg">{selectedContentType.name}</CardTitle>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedContentType(null)}
                    >
                      Change
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground">{selectedContentType.description}</p>
                </CardContent>
              </Card>

              {/* Content Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Tone</label>
                    <select
                      value={contentSettings.tone}
                      onChange={(e) => setContentSettings(prev => ({ ...prev, tone: e.target.value }))}
                      className="w-full mt-1 p-2 border rounded-md text-sm"
                    >
                      <option value="professional">Professional</option>
                      <option value="casual">Casual</option>
                      <option value="friendly">Friendly</option>
                      <option value="authoritative">Authoritative</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Length</label>
                    <select
                      value={contentSettings.length}
                      onChange={(e) => setContentSettings(prev => ({ ...prev, length: e.target.value }))}
                      className="w-full mt-1 p-2 border rounded-md text-sm"
                    >
                      <option value="short">Short</option>
                      <option value="medium">Medium</option>
                      <option value="long">Long</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Style</label>
                    <select
                      value={contentSettings.style}
                      onChange={(e) => setContentSettings(prev => ({ ...prev, style: e.target.value }))}
                      className="w-full mt-1 p-2 border rounded-md text-sm"
                    >
                      <option value="informative">Informative</option>
                      <option value="persuasive">Persuasive</option>
                      <option value="entertaining">Entertaining</option>
                      <option value="educational">Educational</option>
                    </select>
                  </div>
                </CardContent>
              </Card>

              {/* Collaboration History */}
              <Card className="flex-1">
                <CardHeader>
                  <CardTitle className="text-lg">Collaboration</CardTitle>
                  <CardDescription>Chat with AI to refine your content</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                    {collaborationHistory.map((message) => (
                      <div
                        key={message.id}
                        className={`flex gap-2 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                          message.type === 'user' 
                            ? 'bg-primary text-primary-foreground ml-auto' 
                            : message.type === 'system'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : 'bg-muted'
                        }`}>
                          <div className="whitespace-pre-wrap">{message.content}</div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="space-y-2">
                    <textarea
                      ref={textareaRef}
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      placeholder={!currentContent 
                        ? `${selectedContentType.template}\n\nExample: ${selectedContentType.examples[0]}`
                        : "Ask me to refine, expand, or modify the content..."
                      }
                      className="w-full p-3 border rounded-md text-sm resize-none"
                      rows={3}
                      disabled={isGenerating}
                    />
                    <div className="flex gap-2">
                      {!currentContent ? (
                        <Button
                          onClick={handleGenerateContent}
                          disabled={!userInput.trim() || isGenerating}
                          className="flex-1 gap-2"
                        >
                          {isGenerating ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Generate Content
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={handleRefineContent}
                          disabled={!userInput.trim() || isGenerating}
                          className="flex-1 gap-2"
                        >
                          {isGenerating ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Refining...
                            </>
                          ) : (
                            <>
                              <Edit3 className="w-4 h-4" />
                              Refine Content
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Panel - Generated Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="flex-1">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Generated Content</CardTitle>
                    {currentContent && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCopyContent}
                          className="gap-2"
                        >
                          <Copy className="w-4 h-4" />
                          Copy
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {currentContent ? (
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans bg-muted/30 p-4 rounded-lg">
                        {currentContent}
                      </pre>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                        <selectedContentType.icon className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="font-medium text-lg mb-2">Ready to Create</h3>
                      <p className="text-muted-foreground mb-4">
                        Describe what you'd like to create in the chat panel, and I'll generate content for you.
                      </p>
                      <div className="text-sm text-muted-foreground">
                        <strong>Try:</strong> {selectedContentType.examples[Math.floor(Math.random() * selectedContentType.examples.length)]}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

            </div>
          </>
        )}
      </div>

      {/* Stage Completion - Always Available */}
      <div className="max-w-md mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm text-muted-foreground">Progress</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm font-medium">Collaborations: {collaborationCount}</span>
                  {currentContent && <CheckCircle className="w-4 h-4 text-green-600" />}
                </div>
              </div>
            </div>

            {completionMessage && (
              <div className="text-center p-2 bg-primary/10 text-primary rounded-md text-sm mb-4">
                {completionMessage}
              </div>
            )}

            <div className="space-y-2">
              {canComplete && (
                <Button
                  onClick={handleComplete}
                  disabled={isCompleting}
                  className="w-full gap-2"
                >
                  {isCompleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>Complete Stage <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>
              )}

              <Button
                onClick={handleSkip}
                variant="outline"
                disabled={isCompleting}
                className="w-full gap-2"
              >
                {isCompleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>Skip to Next Stage <ArrowRight className="w-4 h-4" /></>
                )}
              </Button>
              
              <Button 
                onClick={() => navigate('/')} 
                variant="ghost" 
                className="w-full gap-2"
              >
                <Home className="w-4 h-4" />
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}